import Link from 'next/link';
import React from 'react';
import type { UrlObject } from 'url';
import { BaseButtonType } from './CustomButton';
import { colors, fonts, themeColors } from "@/stylingConstants";

export enum ButtonVariant {
    Primary = 'primary',
    Secondary = 'secondary',
    Tertiary = 'tertiary'
}

type CustomLinkButtonType = Omit<BaseButtonType, 'onClick'> & {
    href: string | UrlObject | undefined;
    fullWidth?: boolean;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  };

const CustomLinkButton = ({
    isDisabled,
    href,
    onClick,
    children,
    extraButtonClasses,
    variant = ButtonVariant.Primary,
    leftIcon,
    rightIcon,
    extraStyles = {},
    extraDisabledStyles = {},
    fullWidth = true,
    ...props
}: CustomLinkButtonType) => {

    if (!href) {
        return null;
    }

    const getBackgroundClasses = () => {
        if (isDisabled) {
            return "bg-gray-300 cursor-not-allowed";
        }
        if (variant === "primary") {
            return `bg-brightCambridgeBlue hover:bg-CambridgeBlue_50 hover:text-white active:bg-darkCambridgeBlue active:text-white 
      focus:bg-CambridgeBlue_50 focus:text-white focus:border-2 focus-multiple-shadows`;
        }
        if (variant === "secondary") {
            return `bg-white border-2 border-brightCambridgeBlue text-CambridgeBlue_50
      hover:border-semiDarkCambridgeBlue hover:text-semiDarkCambridgeBlue
      active:border-darkCambridgeBlue active:text-darkCambridgeBlue
      focus:border-semiDarkCambridgeBlue focus:text-semiDarkCambridgeBlue
      focus-multiple-shadows`;
        }
        return `bg-transparent text-CambridgeBlue_50 
    hover:text-semiDarkCambridgeBlue active:text-darkCambridgeBlue focus:text-semiDarkCambridgeBlue focus-multiple-shadows`;
    };

    const backgroundClasses = getBackgroundClasses();

    const disabledStyles = variant === 'secondary' 
        ? {
            backgroundColor: themeColors.adult.cupGeneralColor1,
            color: colors.gray2,
            border: `2px solid ${colors.gray2}`,
            cursor: 'not-allowed',
            pointerEvents: 'none' as const,
            ...extraDisabledStyles
        }
        : {
            backgroundColor: colors.gray3,
            color: colors.gray2,
            border: 'none',
            cursor: 'not-allowed',
            pointerEvents: 'none' as const,
            ...extraDisabledStyles
        };

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (isDisabled) {
            event.preventDefault();
            return;
        }
        onClick?.(event);
    };

    return (
        <Link
            href={isDisabled ? '#' : href}
            onClick={handleClick}
            style={{
                width: fullWidth ? '100%' : 'auto',
                flexGrow: 1,
                margin: '0',
                padding: '16px 24px',
                borderRadius: '5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                whiteSpace: 'nowrap',
                maxHeight: 56,
                textDecoration: 'none',
                ...fonts.button,
                ...extraStyles,
                ...(isDisabled ? disabledStyles : {}),
            }}
            className={`${backgroundClasses} ${extraButtonClasses ?? ''}`}
            aria-disabled={isDisabled}
            tabIndex={isDisabled ? -1 : undefined}
            {...props}
        >
            {leftIcon && <span style={{ marginRight: '8px' }}>{leftIcon}</span>}
            {children}
            {rightIcon && <span style={{ marginLeft: '8px' }}>{rightIcon}</span>}
        </Link>
    );
};

export default CustomLinkButton;
